import {
  IChartingData,
  IDentalChart,
  IDentalData,
  IPerioChart,
} from '@dataTypes/chartingFeature.types'
import type { ChartSaveSource } from '@utils/chart-save.utils'
import { default as axios } from './axiosConfig'
import { getEnv } from '@utils/env'
import { logger } from '@utils/safeLogger'
import { toApiError } from './errors/api-error'

interface ApiResponse<T> {
  statusCode: number
  message: string
  data: T
}

// Define the types for the response structure
export type GetChartingResponse = ApiResponse<IChartingData>
export type CreateChartingResponse = ApiResponse<
  IPerioChart | IDentalChart | null
>
export type UpdateChartingResponse = ApiResponse<
  IPerioChart | IDentalChart | null
>
export type ChartingResponse = ApiResponse<Record<string, unknown>>

const BUSINESS_LAYER_API = getEnv('REACT_APP_BUSINESS_LAYER_CHARTING')
const headers = {
  'Content-Type': 'application/json',
}

// Function to get access token
export const getChartsByPatientId = async (
  patientId: string,
): Promise<GetChartingResponse> => {
  try {
    const { data, status } = await axios.get<GetChartingResponse>(
      `${BUSINESS_LAYER_API}/api/v1/charting/patient/${patientId}`,
      {
        headers,
      },
    )
    // Check if the response status is 200, throw an error otherwise
    if (status !== 200) {
      throw new Error('Authentication failed')
    }
    return data
  } catch (e: any) {
    logger.error('error', e)
    throw toApiError(e, 'Error during authentication')
  }
}

export const createPerioChart = async (
  chart: IPerioChart,
  saveSource: ChartSaveSource = 'manual',
): Promise<CreateChartingResponse> => {
  try {
    const { chart_id, ...sanitizedChart } = chart
    const { data, status } = await axios.post<CreateChartingResponse>(
      `${BUSINESS_LAYER_API}/api/v1/charting/perio`,
      { ...sanitizedChart, save_source: saveSource },
      { headers },
    )
    // Check if the response status is 201, throw an error otherwise
    if (status !== 201) {
      throw new Error('Authentication failed')
    }
    return data
  } catch (e: any) {
    logger.error('error', e)
    throw toApiError(e, 'Error during authentication')
  }
}

// Helper function to convert tooth_number from string to number
const convertDentalDataToNumbers = (
  dentalData?: IDentalData[],
):
  | Array<Omit<IDentalData, 'tooth_number'> & { tooth_number: number }>
  | undefined => {
  return dentalData?.map((entry) => ({
    ...entry,
    tooth_number:
      typeof entry.tooth_number === 'string'
        ? parseInt(entry.tooth_number, 10)
        : entry.tooth_number,
  }))
}

export const createDentalChart = async (
  chart: IDentalChart,
  saveSource: ChartSaveSource = 'manual',
): Promise<CreateChartingResponse> => {
  try {
    const { chart_id, ...sanitizedChart } = chart
    // Convert tooth_number from string to number in dental_data
    const sanitizedChartWithNumbers = {
      ...sanitizedChart,
      dental_data: convertDentalDataToNumbers(sanitizedChart.dental_data),
      save_source: saveSource,
    }
    const { data, status } = await axios.post<CreateChartingResponse>(
      `${BUSINESS_LAYER_API}/api/v1/charting/dental`,
      sanitizedChartWithNumbers,
      { headers },
    )
    // Check if the response status is 201, throw an error otherwise
    if (status !== 201) {
      throw new Error('Authentication failed')
    }
    return data
  } catch (e: any) {
    logger.error('error', e)
    throw toApiError(e, 'Error during authentication')
  }
}

export const updateDentalChartById = async (
  id: string,
  chart: IDentalChart,
  saveSource: ChartSaveSource = 'manual',
): Promise<CreateChartingResponse> => {
  try {
    // Convert tooth_number from string to number in dental_data
    const chartWithNumbers = {
      ...chart,
      dental_data: convertDentalDataToNumbers(chart.dental_data),
    }
    const { data, status } = await axios.put<UpdateChartingResponse>(
      `${BUSINESS_LAYER_API}/api/v1/charting/dental`,
      {
        chart_id: id,
        data: chartWithNumbers,
        save_source: saveSource,
      },
      { headers },
    )
    if (status !== 200) {
      throw new Error('Authentication failed')
    }
    return data
  } catch (e: any) {
    logger.error('error', e)
    throw toApiError(e, 'Error during authentication')
  }
}

export const updatePerioChartById = async (
  id: string,
  chart: IPerioChart,
  saveSource: ChartSaveSource = 'manual',
): Promise<CreateChartingResponse> => {
  try {
    const { data, status } = await axios.put<UpdateChartingResponse>(
      `${BUSINESS_LAYER_API}/api/v1/charting/perio`,
      {
        chart_id: id,
        data: chart,
        save_source: saveSource,
      },
      { headers },
    )
    if (status !== 200) {
      throw new Error('Authentication failed')
    }
    return data
  } catch (e: any) {
    logger.error('error', e)
    throw toApiError(e, 'Error during authentication')
  }
}

export const deleteDentalChartById = async (
  chart_id: string,
): Promise<ChartingResponse> => {
  try {
    const { data, status } = await axios.delete<ChartingResponse>(
      `${BUSINESS_LAYER_API}/api/v1/charting/dental/${chart_id}`,
      { headers },
    )
    if (status !== 200) {
      throw new Error('Authentication failed')
    }
    return data
  } catch (e: any) {
    logger.error('error', e)
    throw toApiError(e, 'Error during authentication')
  }
}

export const deletePerioChartById = async (
  chart_id: string,
): Promise<ChartingResponse> => {
  try {
    const { data, status } = await axios.delete<ChartingResponse>(
      `${BUSINESS_LAYER_API}/api/v1/charting/perio/${chart_id}`,
      { headers },
    )
    if (status !== 200) {
      throw new Error('Authentication failed')
    }
    return data
  } catch (e: any) {
    logger.error('error', e)
    throw toApiError(e, 'Error during authentication')
  }
}
